import { useState, useEffect, useRef } from 'react';

const TITLE = 'SIEMPRE CONECTADOS';
const PHRASE = 'No hay nada que podamos lograr en este mundo solos. Cada hecho que sucede es la combinación de nuestra vida personal con la de miles de vínculos y conexiones invisibles con los demás, que son los que hacen llegar a crear algo.';

export function SiempreConectados() {
  const [displayTitle, setDisplayTitle] = useState('');
  const [displayPhrase, setDisplayPhrase] = useState('');
  const [typingPhase, setTypingPhase] = useState<'idle' | 'waiting' | 'title' | 'phrase' | 'done'>('idle');
  const [showCursor, setShowCursor] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          setTypingPhase('waiting');
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typingPhase !== 'waiting') return;
    const timeout = setTimeout(() => {
      setTypingPhase('title');
    }, 1000);
    return () => clearTimeout(timeout);
  }, [typingPhase]);

  useEffect(() => {
    if (typingPhase !== 'title') return;
    let index = 0;
    const interval = setInterval(() => {
      if (index < TITLE.length) {
        setDisplayTitle(TITLE.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setTimeout(() => setTypingPhase('phrase'), 800);
      }
    }, 160);
    return () => clearInterval(interval);
  }, [typingPhase]);

  useEffect(() => {
    if (typingPhase !== 'phrase') return;
    let index = 0;
    const interval = setInterval(() => {
      if (index < PHRASE.length) {
        setDisplayPhrase(PHRASE.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setTypingPhase('done');
      }
    }, 65);
    return () => clearInterval(interval);
  }, [typingPhase]);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  const titleCursor = (
    <span
      className={`inline-block transition-opacity duration-100 ${showCursor ? 'opacity-100' : 'opacity-0'}`}
      style={{ color: '#ffffff', textShadow: '0 0 8px rgba(255,255,255,0.6)' }}
    >
      _
    </span>
  );

  const phraseCursor = (
    <span
      className={`inline-block ml-0.5 transition-opacity duration-100 ${showCursor ? 'opacity-100' : 'opacity-0'}`}
      style={{ color: '#00fff0', textShadow: '0 0 8px rgba(0,255,240,0.6)' }}
    >
      |
    </span>
  );

  return (
    <section
      ref={sectionRef}
      id="siempre-conectados"
      className="relative py-14 sm:py-18 lg:py-22 overflow-hidden"
    >
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_36b3e8adf667c7848eea0f470b753952_4edd50a0.jpg"
          alt="Fondo tecnológico abstracto"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute inset-0 bg-black/65 pointer-events-none"></div>

      <div className="absolute inset-0 bg-gradient-to-b from-gray-950/30 via-transparent to-gray-950/30 pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,255,240,0.04),transparent_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,255,240,0.03),transparent_50%)] pointer-events-none"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <div 
          className="mb-10 sm:mb-14 min-h-[3rem] sm:min-h-[4rem]"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <h4 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[0.2em] sm:tracking-[0.25em] inline">
            <a 
              href="#siempre-conectados" 
              className="transition-colors duration-300"
              style={{ 
                color: isHovered ? '#ffffff' : '#00fff0',
                textShadow: isHovered 
                  ? '0 0 8px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2)' 
                  : '0 0 8px rgba(0,255,240,0.6), 0 0 16px rgba(0,255,240,0.3)'
              }}
            >
              {displayTitle}
            </a>
            {(typingPhase === 'title' || typingPhase === 'phrase' || typingPhase === 'done') && titleCursor}
          </h4>
        </div>

        <div className="min-h-[140px] sm:min-h-[180px]">
          <p
            className="text-lg sm:text-xl lg:text-2xl text-gray-300/90 leading-relaxed sm:leading-loose font-light max-w-3xl mx-auto"
            style={{
              textShadow: '0 0 20px rgba(255,255,255,0.05)',
              fontFamily: '\'Inter\', system-ui, sans-serif',
            }}
          >
            {displayPhrase}
            {(typingPhase === 'phrase') && phraseCursor}
          </p>
        </div>

        {typingPhase === 'done' && (
          <div className="mt-12 sm:mt-16 flex justify-center animate-fade-in-sc">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-neon-400/40 to-transparent"></div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in-sc {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-sc {
          animation: fade-in-sc 1s ease-out forwards;
        }
      `}</style>
    </section>
  );
}
